import React, { useCallback, useRef, useEffect } from 'react';
import { NodeViewWrapper, NodeViewContent, ReactNodeViewRenderer, NodeViewProps } from '@tiptap/react';
import CodeBlockLowlight, { CodeBlockLowlightOptions } from '@tiptap/extension-code-block-lowlight';
import hljs from 'highlight.js'; // Import Highlight.js
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { dispatchTaskSuccess } from '../../../services/taskServices';
import { ICON_SIZE, languageOptions } from '../../../global/constants';

interface CustomCodeBlockOptions extends CodeBlockLowlightOptions {
  isRead: boolean;
}

// Dynamically load highlight.js theme based on app theme
const loadHighlightTheme = () => {
  const existingLink = document.getElementById('highlight-theme');
  const isDark = document.body.classList.contains('dark');
  const themeUrl = isDark
    ? 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/styles/github-dark.min.css'
    : 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/styles/github.min.css';

  if (existingLink) {
    existingLink.setAttribute('href', themeUrl);
  } else {
    const link = document.createElement('link');
    link.id = 'highlight-theme';
    link.rel = 'stylesheet';
    link.href = themeUrl;
    document.head.appendChild(link);
  }
};

// NodeView Component
const CodeBlockWithDropdown: React.FC<NodeViewProps> = ({ node, updateAttributes, extension }) => {
  const languageRef = useRef(node.attrs.language || 'plaintext'); // Default to plaintext

  // Load theme on mount and watch for theme changes
  useEffect(() => {
    loadHighlightTheme();
    // Watch for theme changes on body element
    const observer = new MutationObserver(() => {
      loadHighlightTheme();
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
    return () => observer.disconnect();
  }, []);

  const languageMapping: Record<string, string> = {
    vbscript: 'kotlin',
    pgsql: 'javascript',
    angelscript: 'typescript',
    xml: 'html',
    prolog: 'json',
    plaintext: 'plaintext',
    shell: 'bash',
    fsharp: 'graphql',
    'php-template': 'php',
    n1ql: 'sql',
    arduino: 'java',
    ini: 'toml',
    ceylon: 'swift',
    stylus: 'python',
    lisp: 'clojure',
    livescript: 'haskell',
    clean: 'markdown',
    routeros: 'ruby'
  };

  const getCorrectedLanguage = (detected: string) => {
    return languageOptions.includes(detected) ? detected : languageMapping[detected] || 'plaintext';
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    const pastedContent = event.clipboardData.getData('text');
    if (!pastedContent) return;

    // Detect language using Highlight.js
    const detectedLanguage = hljs.highlightAuto(pastedContent).language;
    const correctedLanguage = getCorrectedLanguage(detectedLanguage || 'plaintext');

    // Update language if it's different
    if (languageRef.current !== correctedLanguage) {
      languageRef.current = correctedLanguage;
      updateAttributes({ language: correctedLanguage });
    }
  };

  const copyText = useCallback(async () => {
    const codeContent = node.textContent || '';
    await navigator.clipboard.writeText(codeContent);
    dispatchTaskSuccess('Code copied successfully!');
  }, [node.textContent]);

  return (
    <NodeViewWrapper className='code-block-with-dropdown'>
      <pre>
        <div className='code-block-header'>
          <div role='button' className='copy-icon' tabIndex={0} onClick={copyText}>
            <SVGIcon
              name='copy-icon'
              width={ICON_SIZE.LG}
              height={ICON_SIZE.LG}
              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
              fill='var(--text-secondary)'
            />
          </div>
        </div>
        <NodeViewContent as='code' onPaste={handlePaste} />
      </pre>
    </NodeViewWrapper>
  );
};

// Extend CodeBlockLowlight to Use Custom NodeView
const CustomCodeBlockLowlight = CodeBlockLowlight.extend<CustomCodeBlockOptions>({
  addOptions() {
    return {
      ...this.parent?.(),
      isRead: false // Default value
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(CodeBlockWithDropdown);
  }
});

export default CustomCodeBlockLowlight;
